import { forwardRef, useId, useImperativeHandle, useRef, useState, type ReactNode } from 'react';

import type { BlockNode, BlockTree } from '@shared/block-renderer';
import { cn } from '@shared/ui/cn';
import { ArrowDownIcon, ArrowUpIcon, TrashIcon } from '@shared/ui/icons';

import { BLOCK_LABELS, createBlock, EDITORIAL_BLOCK_TYPES, type EditorialBlockType } from './blocks';
import { newBlockId } from './body';
import { editorCopy } from './copy';

/**
 * The article body — **one continuous writing surface, not a rack of fields**.
 *
 * The difference is volume, not taste. A page body is a handful of blocks
 * edited occasionally and discrete labelled fields are the most robust way to
 * hold them; an article is dozens of blocks written in one sitting, where a
 * field per block costs a `Tab` before every paragraph. The stored shape does
 * not change either way: every direct child carries its type, and what is saved
 * is a tree of typed blocks rather than page markup.
 *
 * **Text lives in the DOM and structure lives in React, and that split is the
 * whole design.** Re-rendering a `contenteditable` on every keystroke destroys
 * the caret and the browser's own undo stack, and undo belongs to the writer
 * rather than to the page. So typing touches nothing React owns; the tree is
 * read out of the DOM when it is needed — on save, and immediately before any
 * structural change, so a re-render re-seeds the text it just read rather than
 * discarding it.
 *
 * **Media are islands.** An image and a clip are not text; leaving them
 * editable lets a keystroke in the wrong place take a card apart. Their caption
 * is a real `<input>` inside the island, so it stays writable while the card
 * around it does not.
 *
 * The `/` menu is a **second** route, never the only one: it needs JavaScript,
 * and a type the writer cannot see is a type they never use — so the full row
 * of seven stays drawn below the surface.
 */

const TEXT_TYPES = new Set<EditorialBlockType>(['p', 'h3', 'ul', 'quote', 'note']);

export interface FlowHandle {
  /** The tree as it stands right now, text read out of the live DOM. */
  read: () => BlockTree;
}

function textOf(node: BlockNode): string {
  return typeof node.data.text === 'string' ? node.data.text : '';
}

function itemsOf(node: BlockNode): string[] {
  const raw = Array.isArray(node.data.items) ? node.data.items : [];
  const items = raw.filter((item): item is string => typeof item === 'string');
  return items.length > 0 ? items : [''];
}

export const PostEditorFlow = forwardRef<
  FlowHandle,
  {
    /** Seeds the surface once per post; later edits are the DOM's, not React's. */
    initialTree: BlockTree;
    disabled?: boolean;
    onDirty: () => void;
  }
>(function PostEditorFlow({ initialTree, disabled = false, onDirty }, ref) {
  const flowRef = useRef<HTMLDivElement>(null);
  const helpId = useId();
  const labelId = useId();
  const slashId = useId();

  // Structure only. Text is never held here — see the component's note.
  const [blocks, setBlocks] = useState<BlockTree>(() =>
    initialTree.length > 0 ? initialTree : [createBlock('p', newBlockId())],
  );
  const [slashOpen, setSlashOpen] = useState(false);

  /** Walks the live surface and rebuilds the tree from what is on screen. */
  function read(): BlockTree {
    const root = flowRef.current;
    if (!root) return blocks;

    return blocks.map((block) => {
      const element = root.querySelector<HTMLElement>(`[data-block-id="${block.id}"]`);
      if (!element) return block;

      if (block.type === 'ul') {
        const items = Array.from(element.querySelectorAll('li')).map((li) => li.textContent ?? '');
        return { ...block, data: { ...block.data, items: items.length > 0 ? items : [''] } };
      }

      if (block.type === 'quote') {
        const cite = element.querySelector('cite');
        const whole = element.textContent ?? '';
        const citeText = cite?.textContent ?? '';
        return {
          ...block,
          data: {
            ...block.data,
            text: whole.slice(0, whole.length - citeText.length).trim(),
            cite: citeText,
          },
        };
      }

      if (block.type === 'image' || block.type === 'video') {
        const caption = element.querySelector<HTMLInputElement>('input[data-caption]');
        return { ...block, data: { ...block.data, caption: caption?.value ?? '' } };
      }

      return { ...block, data: { ...block.data, text: element.textContent ?? '' } };
    });
  }

  useImperativeHandle(ref, () => ({ read }));

  /**
   * Every structural change reads the surface first. Without that read the
   * re-render below would re-seed each block from the state it was created
   * with, and every word typed since would be gone — silently, on a button the
   * writer pressed for an unrelated reason.
   */
  function mutate(apply: (current: BlockTree) => BlockTree) {
    setBlocks(apply(read()));
    onDirty();
  }

  function append(type: EditorialBlockType) {
    mutate((current) => [...current, createBlock(type, newBlockId())]);
    setSlashOpen(false);
  }

  function move(index: number, delta: -1 | 1) {
    const target = index + delta;
    mutate((current) => {
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(index, 1);
      if (moved) next.splice(target, 0, moved);
      return next;
    });
  }

  function remove(index: number) {
    mutate((current) => {
      const next = current.filter((_block, position) => position !== index);
      return next.length > 0 ? next : [createBlock('p', newBlockId())];
    });
  }

  return (
    <div className="relative">
      <p id={labelId} className="sr-only">
        {editorCopy.bodyLabel}
      </p>

      <div
        ref={flowRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        // A `contenteditable` region is tabbable natively; the attribute is
        // written out so the role and the tab stop are declared together, and
        // it stays reachable while a save is in flight — the writer can still
        // read what they are waiting on.
        tabIndex={0}
        aria-multiline="true"
        aria-labelledby={labelId}
        aria-describedby={helpId}
        onInput={onDirty}
        onKeyDown={(event) => {
          // «/» on an empty line opens the insert menu. It is checked against
          // the caret's own block rather than the surface, so typing a slash
          // mid-sentence stays a slash.
          if (event.key !== '/') return;
          const selection = window.getSelection();
          const node = selection?.anchorNode;
          const host = node instanceof Element ? node : node?.parentElement;
          if (host?.closest('[data-block-id]')?.textContent?.trim() === '') {
            event.preventDefault();
            setSlashOpen(true);
          }
        }}
        className="min-h-[14rem] rounded-md border border-transparent p-3 transition-colors duration-fast focus-within:border-border-strong hover:border-border focus:border-border-strong focus:outline-none [&>*:last-child]:mb-0 [&>*]:mb-4 [&>*]:max-w-measure"
      >
        {blocks.map((block, index) => (
          <BlockElement
            key={block.id}
            block={block}
            disabled={disabled}
            onMoveUp={() => move(index, -1)}
            onMoveDown={() => move(index, 1)}
            onRemove={() => remove(index)}
            onDirty={onDirty}
          />
        ))}
      </div>

      {slashOpen ? (
        <div
          id={slashId}
          role="listbox"
          aria-label={editorCopy.slashLabel}
          className="absolute z-[60] w-[15rem] rounded-md border border-border bg-surface-overlay p-2 shadow-3"
        >
          {EDITORIAL_BLOCK_TYPES.map((type, index) => (
            <button
              key={type}
              type="button"
              role="option"
              aria-selected={index === 0}
              onClick={() => append(type)}
              className={cn(
                'hit-target flex w-full items-center rounded-sm border-s-2 border-transparent px-3 py-2 text-start text-sm',
                index === 0
                  ? 'border-s-accent bg-accent-soft font-semibold text-accent-text'
                  : 'text-ink-muted hover:bg-hover-bg hover:text-ink',
              )}
            >
              {BLOCK_LABELS[type]}
            </button>
          ))}
        </div>
      ) : null}

      <p id={helpId} className="mb-0 mt-3 max-w-measure text-xs text-ink-muted">
        {editorCopy.flowHelp}
      </p>

      <div
        role="group"
        aria-label={editorCopy.addRowLabel}
        className="mt-4 flex flex-wrap items-center gap-2 border-t border-dashed border-border pt-4"
      >
        <span className="text-xs text-ink-muted">{editorCopy.addRowLead}</span>
        {EDITORIAL_BLOCK_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            disabled={disabled}
            onClick={() => append(type)}
            className="min-h-6 rounded-sm border border-border-strong bg-surface-raised px-3 text-xs font-semibold text-ink hover:bg-surface-overlay disabled:cursor-not-allowed disabled:opacity-45"
          >
            {BLOCK_LABELS[type]}
          </button>
        ))}
      </div>
    </div>
  );
});

function BlockElement({
  block,
  disabled,
  onMoveUp,
  onMoveDown,
  onRemove,
  onDirty,
}: {
  block: BlockNode;
  disabled: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  onDirty: () => void;
}) {
  const type = block.type as EditorialBlockType;
  const common = { 'data-block-id': block.id, 'data-block': block.type } as const;

  if (TEXT_TYPES.has(type)) {
    if (type === 'h3') {
      return (
        <h3 {...common} suppressContentEditableWarning className="font-display text-h3 font-semibold">
          {textOf(block)}
        </h3>
      );
    }

    if (type === 'ul') {
      return (
        <ul {...common} suppressContentEditableWarning className="list-disc ps-5">
          {itemsOf(block).map((item, index) => (
            <li key={index} className="mb-2">
              {item}
            </li>
          ))}
        </ul>
      );
    }

    if (type === 'quote') {
      return (
        <blockquote
          {...common}
          suppressContentEditableWarning
          className="border-s-2 border-border-strong ps-4 text-ink-muted"
        >
          {textOf(block)}
          <cite className="mt-2 block text-sm not-italic">
            {typeof block.data.cite === 'string' ? block.data.cite : ''}
          </cite>
        </blockquote>
      );
    }

    if (type === 'note') {
      return (
        <aside
          {...common}
          suppressContentEditableWarning
          className="rounded-sm border-s-[3px] border-info bg-info-soft p-4 text-sm"
        >
          {textOf(block)}
        </aside>
      );
    }

    return (
      <p
        {...common}
        suppressContentEditableWarning
        data-placeholder={editorCopy.paragraphPlaceholder}
        className="empty:before:text-ink-faint empty:before:content-[attr(data-placeholder)]"
      >
        {textOf(block)}
      </p>
    );
  }

  // The two islands. `contentEditable={false}` is what keeps a stray keystroke
  // from dismantling the card; the caption below it is a real field.
  const kind = BLOCK_LABELS[type] ?? block.type;

  return (
    <MediaIsland
      {...common}
      kind={kind}
      caption={typeof block.data.caption === 'string' ? block.data.caption : ''}
      play={type === 'video'}
      disabled={disabled}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      onRemove={onRemove}
      onDirty={onDirty}
    />
  );
}

function MediaIsland({
  kind,
  caption,
  play,
  disabled,
  onMoveUp,
  onMoveDown,
  onRemove,
  onDirty,
  ...rest
}: {
  kind: string;
  caption: string;
  play: boolean;
  disabled: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  onDirty: () => void;
} & Record<`data-${string}`, string>) {
  const captionId = useId();

  return (
    <figure
      {...rest}
      contentEditable={false}
      className="group my-5 rounded-md border border-border bg-surface p-3"
    >
      <div className="mb-3 flex items-center gap-2">
        <span className="mono text-xs text-ink-faint">{kind}</span>
        {/*
          The handles fade in on hover and on focus-within, never `display:
          none`: hidden that way they leave the tab order, and a keyboard writer
          could not reach the only control that moves a block.
        */}
        <span className="ms-auto flex gap-1 opacity-100 transition-opacity duration-fast group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:hover)]:opacity-0">
          <IslandButton label={editorCopy.moveUp(kind)} disabled={disabled} onClick={onMoveUp}>
            <ArrowUpIcon />
          </IslandButton>
          <IslandButton label={editorCopy.moveDown(kind)} disabled={disabled} onClick={onMoveDown}>
            <ArrowDownIcon />
          </IslandButton>
          <IslandButton label={editorCopy.removeBlock(kind)} disabled={disabled} onClick={onRemove}>
            <TrashIcon />
          </IslandButton>
        </span>
      </div>

      <span
        aria-hidden="true"
        className="grid aspect-video w-full place-items-center rounded-sm border border-border bg-[linear-gradient(145deg,color-mix(in_srgb,var(--brand-noir)_72%,var(--brand-bone)_28%),color-mix(in_srgb,var(--brand-noir)_90%,var(--brand-bone)_10%)_55%,color-mix(in_srgb,var(--brand-noir)_80%,var(--brand-indigo)_20%))] text-[var(--brand-bone)]"
      >
        {play ? (
          <svg viewBox="0 0 24 24" fill="currentColor" className="size-10 opacity-85" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
        ) : null}
      </span>

      <figcaption className="mt-3">
        <label className="sr-only" htmlFor={captionId}>
          {editorCopy.captionLabel(kind)}
        </label>
        <input
          id={captionId}
          data-caption=""
          type="text"
          defaultValue={caption}
          disabled={disabled}
          onInput={onDirty}
          className="hit-target w-full rounded-sm border border-border-strong bg-surface-raised px-4 text-body text-ink disabled:opacity-45"
        />
      </figcaption>
    </figure>
  );
}

function IslandButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-8 items-center justify-center rounded-sm text-ink-muted hover:bg-hover-bg hover:text-ink disabled:opacity-30"
    >
      {children}
    </button>
  );
}
